import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Details, CalendarMonth, Paid, GridView } from "@mui/icons-material";
import { formatDateOutput } from "../../../Utils/formatDate";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  {
    id: "details",
    label: "Details",
    icon: <Details sx={{ marginRight: 1 }} />,
  },
  {
    id: "expenseCategoryName",
    label: "Expense Category Name",
    icon: <GridView sx={{ marginRight: 1 }} />,
  },
  {
    id: "expenseDate",
    label: "Expense Date",
    icon: <CalendarMonth sx={{ marginRight: 1 }} />,
  },
  {
    id: "amount",
    label: "Amount",
    icon: <Paid sx={{ marginRight: 1 }} />,
  },
];

function ExpenseTable({ expenses, fetchExpenses, expenseCategory }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortedData, setSortedData] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    if (expenses?.data) {
      const sorted = [...expenses.data].sort(
        (a, b) => new Date(b.expenseDate) - new Date(a.expenseDate)
      );
      setSortedData(sorted);
    }
  }, [expenses]);

  const getCategoryName = (categoryId) => {
    const category = expenseCategory.find((cat) => cat.id === categoryId);
    return category ? category.name : "Miscellaneous";
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc =
      sortedData[0][property] > sortedData[sortedData.length - 1][property];
    const sorted = [...sortedData].sort((a, b) => {
      if (isAsc) {
        return new Date(a[property]) - new Date(b[property]);
      } else {
        return new Date(b[property]) - new Date(a[property]);
      }
    });
    setSortedData(sorted);
  };

  const slicedData = sortedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <TableSortLabel
                        active={headCell.id === "expenseDate"}
                        direction="desc"
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        <Box
                          className="table-head"
                          display="flex"
                          alignItems="center"
                        >
                          {headCell.icon}
                          {headCell.label}
                        </Box>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.details}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {getCategoryName(row?.expenseCategoryId)}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {formatDateOutput(row?.expenseDate)}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.amount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={sortedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

ExpenseTable.propTypes = {
  expenses: PropTypes.object.isRequired,
  fetchExpenses: PropTypes.func.isRequired,
  expenseCategory: PropTypes.array.isRequired,
};

export default ExpenseTable;
